import { useState, useEffect } from 'react';
import { Upload, X } from 'lucide-react';
import { Button } from './Button';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';

export const BrandingSettings = () => {
  const [logo, setLogo] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [appTitle, setAppTitle] = useState('');
  const { t } = useLanguage();
  const toast = useToast();

  useEffect(() => {
    // Load saved branding from localStorage
    const savedLogo = localStorage.getItem('app_logo');
    const savedFavicon = localStorage.getItem('app_favicon');
    const savedTitle = localStorage.getItem('app_title');
    
    if (savedLogo) {
      setLogoPreview(savedLogo);
    }
    if (savedFavicon) {
      setFaviconPreview(savedFavicon);
      updateFavicon(savedFavicon);
    }
    if (savedTitle) {
      setAppTitle(savedTitle);
    }
  }, []);

  const updateFavicon = (faviconUrl) => {
    // Update the favicon in the document
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error(t('fileTooLarge') || 'File size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        setLogo(file);
        setLogoPreview(result);
        localStorage.setItem('app_logo', result);
        // Dispatch custom event to update navbar
        window.dispatchEvent(new Event('logoUpdated'));
        toast.success(t('logoUpdated') || 'Logo updated successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 512 * 1024) { // 512KB limit for favicon
        toast.error(t('faviconTooLarge') || 'Favicon size must be less than 512KB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        setFavicon(file);
        setFaviconPreview(result);
        localStorage.setItem('app_favicon', result);
        updateFavicon(result);
        toast.success(t('faviconUpdated') || 'Favicon updated successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setLogoPreview(null);
    localStorage.removeItem('app_logo');
    // Dispatch custom event to update navbar
    window.dispatchEvent(new Event('logoUpdated'));
    toast.success(t('logoRemoved') || 'Logo removed successfully');
  };

  const handleRemoveFavicon = () => {
    setFavicon(null);
    setFaviconPreview(null);
    localStorage.removeItem('app_favicon');
    // Reset to default favicon
    updateFavicon('/favicon.ico');
    toast.success(t('faviconRemoved') || 'Favicon removed successfully');
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setAppTitle(newTitle);
    localStorage.setItem('app_title', newTitle);
    // Dispatch custom event to update navbar
    window.dispatchEvent(new Event('titleUpdated'));
    toast.success(t('titleUpdated') || 'Title updated successfully');
  };

  const handleRemoveTitle = () => {
    setAppTitle('');
    localStorage.removeItem('app_title');
    // Dispatch custom event to update navbar
    window.dispatchEvent(new Event('titleUpdated'));
    toast.success(t('titleRemoved') || 'Title removed successfully');
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {t('brandingSettings') || 'Branding Settings'}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('brandingDescription') || 'Customize your application logo and favicon'}
        </p>
      </div>

      {/* Logo Upload */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('applicationLogo') || 'Application Logo'}
        </h3>
        
        <div className="flex items-start gap-6">
          {/* Logo Preview */}
          <div className="flex-shrink-0">
            {logoPreview ? (
              <div className="relative">
                <img
                  src={logoPreview}
                  alt="Logo"
                  className="w-48 h-48 object-contain border-2 border-gray-300 rounded-lg p-2 bg-white"
                />
                <button
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  title={t('remove') || 'Remove'}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">{t('noLogo') || 'No logo uploaded'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="flex-1">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('uploadLogo') || 'Upload Logo'}
              </label>
              <p className="text-sm text-gray-500 mb-3">
                {t('logoRequirements') || 'Recommended: PNG or SVG, max 2MB, square format (e.g., 512x512px)'}
              </p>
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleLogoChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Application Title */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('applicationTitle') || 'Application Title'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('customTitle') || 'Custom Title'}
            </label>
            <p className="text-sm text-gray-500 mb-3">
              {t('titleRequirements') || 'Enter a custom title to display alongside your logo in the navigation bar'}
            </p>
            <div className="flex gap-3">
              <input
                type="text"
                value={appTitle}
                onChange={handleTitleChange}
                placeholder={t('titlePlaceholder') || 'e.g., Lead Management System'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {appTitle && (
                <Button
                  variant="outline"
                  onClick={handleRemoveTitle}
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  {t('remove') || 'Remove'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Favicon Upload */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('favicon') || 'Favicon'}
        </h3>
        
        <div className="flex items-start gap-6">
          {/* Favicon Preview */}
          <div className="flex-shrink-0">
            {faviconPreview ? (
              <div className="relative">
                <img
                  src={faviconPreview}
                  alt="Favicon"
                  className="w-16 h-16 object-contain border-2 border-gray-300 rounded-lg p-2 bg-white"
                />
                <button
                  onClick={handleRemoveFavicon}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  title={t('remove') || 'Remove'}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <Upload className="w-6 h-6 text-gray-400" />
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="flex-1">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('uploadFavicon') || 'Upload Favicon'}
              </label>
              <p className="text-sm text-gray-500 mb-3">
                {t('faviconRequirements') || 'Recommended: ICO or PNG, max 512KB, 32x32px or 16x16px'}
              </p>
              <input
                type="file"
                accept="image/x-icon,image/png"
                onChange={handleFaviconChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>{t('note') || 'Note'}:</strong> {t('brandingNote') || 'Your branding will be saved in browser storage and applied across the application. Clear browser data will reset these settings.'}
        </p>
      </div>
    </div>
  );
};
